(function () {
	'use strict';

	class CartCountdown extends HTMLElement {
		constructor() {
			super();

			// Configuration with defaults
			this.config = {
				countdownMinutes: window?.TCP?.cartCountdownMinutes || 10,
				colorScheme: window?.TCP?.cartCountdownColorScheme || 'green',
				expiredMessage:
					window?.TCP?.cartCountdownExpiredMessage ||
					'Your cart is expiring soon! Checkout to claim these items before they sell out.',
				originalMessage:
					window?.TCP?.cartCountdownMessage ||
					'Your cart will expire in {time}! Checkout now before your items sell out.',
				resetDelayDays: window?.TCP?.cartCountdownReset || 1,
			};

			// Split message at {time} placeholder
			this.splitMessage = this.config.originalMessage.split('{time}');
			this.messageBeforeTime = this.splitMessage[0] || '';
			this.messageAfterTime = this.splitMessage.length > 1 ? this.splitMessage[1] : '';

			// State
			this.timeRemaining = 0;
			this.countdownInterval = null;
			this.isInitialized = false;

			// DOM elements
			this.messageBeforeElement = null;
			this.timerDisplayElement = null;
			this.messageAfterElement = null;
			this.expiredMessageElement = null;
		}

		// Color scheme classes
		get colorSchemes() {
			return {
				green: 'bg-success-100 text-success-900',
				brown: 'bg-dark-100 text-dark-900',
				red: 'bg-red-50 text-red-900',
				gold: 'bg-warning-100 text-warning-900',
			};
		}

		connectedCallback() {
			this.init();
		}

		disconnectedCallback() {
			this.destroy();
		}

		// Initialize target time in localStorage
		initializeTargetTime() {
			const storageKey = 'cartCountdownTarget';
			const storedTarget = localStorage.getItem(storageKey);
			const now = Date.now();
			const resetDelayMs = this.config.resetDelayDays * 24 * 60 * 60 * 1000; // Convert days to milliseconds

			if (storedTarget) {
				const targetTime = parseInt(storedTarget);
				const timeSinceExpiry = now - targetTime;

				// If target time is in the future, use it
				if (targetTime > now) {
					return targetTime;
				}

				// If not enough time has passed since expiry, don't reset
				if (timeSinceExpiry < resetDelayMs) {
					return targetTime; // Return expired time so countdown shows 0
				}
			}

			// Set new target time (countdownMinutes from now)
			const newTargetTime = now + this.config.countdownMinutes * 60 * 1000;
			localStorage.setItem(storageKey, newTargetTime.toString());
			return newTargetTime;
		}

		// Format time remaining as MM:SS
		formatTime(milliseconds) {
			if (milliseconds <= 0) return '0:00';

			const totalSeconds = Math.ceil(milliseconds / 1000);
			const minutes = Math.floor(totalSeconds / 60);
			const seconds = totalSeconds % 60;

			return `${minutes}:${seconds.toString().padStart(2, '0')} minutes`;
		}

		// Update countdown display
		updateCountdown() {
			const targetTime = this.initializeTargetTime();
			const now = Date.now();
			this.timeRemaining = Math.max(0, targetTime - now);

			// Update display
			this.updateDisplay();

			// Clear interval if countdown is complete
			if (this.timeRemaining <= 0) {
				if (this.countdownInterval) {
					clearInterval(this.countdownInterval);
					this.countdownInterval = null;
				}
			}
		}

		// Update the visual display
		updateDisplay() {
			if (!this.isInitialized) return;

			const showTimer =
				this.config.countdownMinutes > 0 && this.splitMessage.length > 1 && this.timeRemaining > 0;

			if (showTimer) {
				// Show timer
				if (this.messageBeforeElement) {
					this.messageBeforeElement.innerHTML = this.messageBeforeTime;
					this.messageBeforeElement.style.display = this.messageBeforeTime ? 'inline' : 'none';
				}
				if (this.timerDisplayElement) {
					this.timerDisplayElement.innerHTML = this.formatTime(this.timeRemaining);
					this.timerDisplayElement.style.display = 'inline';
				}
				if (this.messageAfterElement) {
					this.messageAfterElement.innerHTML = this.messageAfterTime;
					this.messageAfterElement.style.display = this.messageAfterTime ? 'inline' : 'none';
				}

				// Show/hide elements
				if (this.expiredMessageElement) {
					this.expiredMessageElement.innerHTML = this.config.expiredMessage;
					this.expiredMessageElement.style.display = 'none';
				}
			} else {
				// Show expired message
				if (this.expiredMessageElement) {
					this.expiredMessageElement.innerHTML = this.config.expiredMessage;
					this.expiredMessageElement.style.display = 'inline';
				}

				// Show/hide elements
				if (this.messageBeforeElement) {
					this.messageBeforeElement.style.display = 'none';
				}
				if (this.timerDisplayElement) {
					this.timerDisplayElement.style.display = 'none';
				}
				if (this.messageAfterElement) {
					this.messageAfterElement.style.display = 'none';
				}
				if (this.expiredMessageElement) {
					this.expiredMessageElement.style.display = 'inline';
				}
			}

			this.classList.remove('hidden');
			this.classList.add('block');
		}

		// Initialize the countdown
		init() {
			// Get DOM elements
			this.messageBeforeElement = this.querySelector('.message-before-time');
			this.timerDisplayElement = this.querySelector('.timer-display');
			this.messageAfterElement = this.querySelector('.message-after-time');
			this.expiredMessageElement = this.querySelector('.expired-message');

			// Apply color scheme
			const colorClass = this.colorSchemes[this.config.colorScheme] || this.colorSchemes.green;
			this.className = `px-4 py-2.5 rounded ${colorClass}`;

			// Initialize countdown immediately
			this.updateCountdown();

			// Set up interval for countdown updates
			this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);

			this.isInitialized = true;
		}

		// Clean up function
		destroy() {
			if (this.countdownInterval) {
				clearInterval(this.countdownInterval);
				this.countdownInterval = null;
			}
			this.isInitialized = false;
		}

		// Public methods for external control
		reset() {
			localStorage.removeItem('cartCountdownTarget');
			this.updateCountdown();
		}
	}

	// Register the custom element
	customElements.define('cart-countdown', CartCountdown);

	// Expose for external use
	window.CartCountdown = CartCountdown;
})();
